import CircleIcon from '@mui/icons-material/Circle';
import DownloadIcon from '@mui/icons-material/Download';
import { Timeline, TimelineItem } from '@mui/lab';
import {
  Alert,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  BiDetail,
  BiEnvelope,
  BiHistory,
  BiMessageDetail,
  BiPhone,
  BiTimeFive,
} from 'react-icons/bi';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getClientInvoiceInfo,
  getPresignedURLForPreview,
  IClientInvoice,
  getPresignedURLForGet,
  updateInvoiceStatus,
  getActivityLogs,
  ActivityLogDTO,
  getActivityLogsByEntity,
} from 'wisestep-api';
import {
  formattedDate,
  ScrollableTabs,
  TabTextVariant,
  WsColors,
  WsTitleBanner,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsIconText,
  WsButton,
  WsDialog,
  WsDialogSize,
  ControlledTextArea,
  WsTimeLine,
  formatDateInDayMonthYearTime,
} from 'wisestep-base';

import CommunicationBar from '../../../components/communicationBar/CommunicationBar';
import Notes, { NotesParent } from '../../../components/communicationBar/Notes';
import { WsActivityProps } from '../../../features/activityLog/ActivityHelper';
import { useInvoicingAuthz, InvoicingAction } from '../../../hooks/useInvoicingAuthz';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import ContractorInvoiceDetailRelatedInvoicesContainer from './invoiceDetails/ContractorInvoiceDetailRelatedInvoicesContainer';
import InvoiceDetailContractPlacementDetails from './invoiceDetails/InvoiceDetailContractPlacementDetails';
import OtherDocuments from './OtherDocuments';
import PlacementDetails from './PlacementDetails';
import RelatedInvoicesContainer from './RelatedInvoicesContainer';
import TranscationDetails from './TranscationDetails';

interface IInvoiceStatusDetail {
  status: string;
  message: string;
  label: string;
}

interface UpdateStatusObj {
  invoiceId: string;
  reason: string;
  status: string;
}

const InvoiceDetails = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [invoice, setInvoice] = useState<IClientInvoice>();
  const [uri, setUri] = useState<string>();
  const [tabPanelPage, setTabPanelPage] = useState<number>(0);
  const [showOnHoldDialog, setShowOnHoldDialog] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<IInvoiceStatusDetail>();
  const methods = useForm<UpdateStatusObj>();
  const [getActivityData, setgetActivityData] = useState<ActivityLogDTO[]>([]);
  const activityLists: WsActivityProps[] = [];

  const invoiceStatusDetails: IInvoiceStatusDetail[] = [
    {
      status: 'ON_HOLD',
      message:
        'Marking Invoice HOLD will disable the Invoice to be added to Payrun or Record Transaction unless the ONHOLD status is withdrawn. The user will be able to VOID the Invoice.',
      label: 'Hold Invoice',
    },
    {
      status: 'VOID',
      message:
        'Marking Invoice VOID will disable the Invoice to be added to Payrun, Record Transaction or mark Invoice On Hold.',
      label: 'Void Invoice',
    },
    {
      status: 'ISSUED',
      message: 'Are you sure you want to unhold the invoice?',
      label: 'Confirm status change',
    },
  ];
  const isAuthorizedViewPlacementDetails = useInvoicingAuthz(
    InvoicingAction.VIEW_INVOICE_DETAILS_PLACEMENT_DETAILS
  );
  const isAuthorizedViewRelatedInvoice = useInvoicingAuthz(
    InvoicingAction.VIEW_INVOICE_DETAILS_RELATED_INVOICES
  );
  const isAuthorizedViewOtherInvoices = useInvoicingAuthz(
    InvoicingAction.VIEW_INVOICE_DETAILS_OTHER_DOCUMENTS
  );
  const isAuthorizedViewTranscationDetasil = useInvoicingAuthz(
    InvoicingAction.VIEW_INVOICE_DETAILS_TRANSACTION_DETAILS
  );

  useEffect(() => {
    let avtivityLog: any = [];
    (async () => {
      const { data: invoiceResponse } = await getClientInvoiceInfo(params.id ?? '');
      setInvoice(invoiceResponse);
      const {
        data: { preSignedUrl },
      } = await getPresignedURLForPreview({ fileName: invoiceResponse.pdfFile?.location ?? '' });
      setUri(preSignedUrl);
      const { data: actvityResponseContext } = await getActivityLogs(params.id ?? '');
      const { data: actvityResponseEnitiy } = await getActivityLogsByEntity(params.id ?? '');
      avtivityLog = actvityResponseContext.concat(actvityResponseEnitiy);
      const uniqueAvtivityLog = avtivityLog.filter((obj, index) => {
        return index === avtivityLog.findIndex((o) => obj.id === o.id);
      });
      setgetActivityData(uniqueAvtivityLog);
    })();
  }, [params]);

  const getPdfDownload = async () => {
    const {
      data: { preSignedUrl },
    } = await getPresignedURLForGet({
      fileName: String(invoice?.pdfFile?.location),
      downloadAs: invoice?.invoiceCode ?? 'invoice',
    });
    window.open(preSignedUrl, '_blank');
  };

  const showUpdateStatusPopup = (status: string) => {
    const selectedStatus: IInvoiceStatusDetail | undefined = invoiceStatusDetails.find((e) => {
      if (e.status === status) {
        return e;
      }
    });
    setSelectedStatus(selectedStatus);
    setShowOnHoldDialog(true);
  };

  const onUpdateStatus = async (data: UpdateStatusObj) => {
    data.invoiceId = params.id ?? '';
    data.status = selectedStatus?.status ?? '';
    try {
      await updateInvoiceStatus(data);
      setNotification(success(`Invoice status successfully updated.`));
      methods.setValue('reason', '');
      setShowOnHoldDialog(false);
      navigate('/invoice/' + invoice?.id);
    } catch {
      setNotification(failed(`Something went wrong while updating invoice status.`));
    }
  };

  getActivityData?.map((data: ActivityLogDTO) =>
    activityLists.push({
      activity: (
        <TimelineItem>
          <Grid container>
            <Grid item xs={12}>
              <WsTypography color={WsTypoColors.TERTIARY}>
                {` At ${formatDateInDayMonthYearTime(data?.createdOn!.toString())} by ${
                  data.createdByName
                }`}
              </WsTypography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant={WsTypoVariants.SUB_TITLE}>
                <>{`${data.field} Category -> ${data.description} ${data.parentEntitiyName}`}</>
              </Typography>
            </Grid>
          </Grid>
        </TimelineItem>
      ),
      leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
    })
  );

  if (!params.id) return null;
  return (
    <Grid container sx={{ display: 'flex', py: 1 }}>
      <Grid item xs={12}>
        <WsTitleBanner
          title={invoice?.invoiceCode ?? ''}
          goBack={() => navigate('/invoice/list')}
          leftChildren={
            <WsTypography color={WsTypoColors.PRIMARY200}>
              <WsIconText
                popOverText="Download Invoice"
                text=""
                textCursor="pointer"
                onClick={getPdfDownload}
                icon={<DownloadIcon />}
              />
            </WsTypography>
          }
          rightChildren={
            <Stack direction="row" spacing={2} alignItems="right">
              {invoice?.invoiceStage === 'ON_HOLD' && (
                <Grid>
                  <WsButton
                    type="button"
                    variant="contained"
                    size="medium"
                    onClick={() => showUpdateStatusPopup('ISSUED')}
                  >
                    Unhold Invoice
                  </WsButton>
                </Grid>
              )}
              {invoice?.invoiceStage !== 'VOID' && invoice?.invoiceStage !== 'ON_HOLD' && (
                <Grid>
                  <WsButton
                    type="button"
                    variant="contained"
                    size="medium"
                    onClick={() => showUpdateStatusPopup('ON_HOLD')}
                  >
                    Hold Invoice
                  </WsButton>
                </Grid>
              )}
              {invoice?.invoiceStage !== 'VOID' && (
                <Grid>
                  <WsButton
                    type="button"
                    variant="contained"
                    size="medium"
                    onClick={() => showUpdateStatusPopup('VOID')}
                  >
                    Void Invoice
                  </WsButton>
                </Grid>
              )}
              <Stack alignItems="flex-end">
                <WsTypography color={WsTypoColors.PRIMARY200} variant={WsTypoVariants.TITLE}>
                  <b>{formattedDate(String(invoice?.dueDate))}</b>
                </WsTypography>
                <WsTypography color={WsTypoColors.PRIMARY200} opacity>
                  Due Date
                </WsTypography>
              </Stack>
            </Stack>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Card sx={{ backgroundColor: WsColors.TheBlues25 }} elevation={0}>
          <ScrollableTabs
            variant={TabTextVariant.SUB_TITLE}
            scrollList={[
              ...(isAuthorizedViewPlacementDetails ? [{ tabName: 'Placement Details' }] : []),
              ...(isAuthorizedViewRelatedInvoice ? [{ tabName: 'Related Invoices' }] : []),
              ...(isAuthorizedViewTranscationDetasil ? [{ tabName: 'Transaction Details' }] : []),
              ...(isAuthorizedViewOtherInvoices ? [{ tabName: 'Other Documents' }] : []),
            ]}
            getTabIndex={(index: number) => {
              setTabPanelPage(index);
            }}
            defaultValue={tabPanelPage}
          />
          {(invoice?.invoiceStage === 'ON_HOLD' || invoice?.invoiceStage === 'VOID') && (
            <Grid container columnSpacing={6} p={2}>
              {invoice?.invoiceStage === 'ON_HOLD' && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {invoice?.invoiceStage + ': ' + invoice?.onHoldReason}
                  </Alert>
                </Grid>
              )}
              {invoice?.invoiceStage === 'VOID' && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {invoice?.invoiceStage + ': ' + invoice?.voidReason}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
          <Grid container columnSpacing={6} p={2}>
            <Grid item xs={6}>
              <iframe
                title="Preview"
                style={{
                  overflowY: 'hidden',
                  width: '100%',
                  height: '1000px',
                  border: 'none',
                }}
                src={uri}
              />
            </Grid>
            <Grid item xs={6}>
              {tabPanelPage == 0 &&
                invoice &&
                (invoice.placementType === 'PERMANENT' ? (
                  <PlacementDetails invoiceId={params.id} />
                ) : (
                  <InvoiceDetailContractPlacementDetails invoice={invoice} />
                ))}
              {tabPanelPage == 1 &&
                invoice &&
                (invoice.placementType === 'PERMANENT' ? (
                  <RelatedInvoicesContainer invoiceId={params.id} />
                ) : (
                  <ContractorInvoiceDetailRelatedInvoicesContainer invoice={invoice} />
                ))}
              {tabPanelPage == 2 && <TranscationDetails invoiceId={params.id} invoice={invoice} />}
              {tabPanelPage == 3 && <OtherDocuments invoiceId={params.id} />}
            </Grid>
          </Grid>
        </Card>
        <CommunicationBar>
          <CommunicationBar.Item title="Activity log!" icon={BiHistory}>
            <Box m={2}>
              {getActivityData && (
                <Timeline>
                  <Box ml="-100%">
                    <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                  </Box>
                </Timeline>
              )}
            </Box>
          </CommunicationBar.Item>
          <CommunicationBar.Item title="Notes" icon={BiDetail}>
            <Notes parent={NotesParent.INVOICE} parentId={params.id} />
          </CommunicationBar.Item>
          <CommunicationBar.Item title="Coming soon!" icon={BiTimeFive}>
            <></>
          </CommunicationBar.Item>
          <CommunicationBar.Item title="Coming soon!" icon={BiMessageDetail}>
            <></>
          </CommunicationBar.Item>
          <CommunicationBar.Item title="Coming soon!" icon={BiEnvelope}>
            <></>
          </CommunicationBar.Item>
          <CommunicationBar.Item title="Coming soon!" icon={BiPhone}>
            <></>
          </CommunicationBar.Item>
        </CommunicationBar>
      </Grid>
      <WsDialog openDialog={showOnHoldDialog} size={WsDialogSize.SMALL}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onUpdateStatus)}>
            <DialogTitle>{selectedStatus?.label}</DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              <WsTypography variant={WsTypoVariants.BODY1}>{selectedStatus?.message}</WsTypography>
              <Grid item xs={12}></Grid>
              {selectedStatus?.status !== 'ISSUED' && (
                <Grid item xs={12}>
                  <ControlledTextArea
                    minRows={10}
                    labelText="Reason :"
                    name="reason"
                    placeholder={'Provide a reason to mark invoice ' + selectedStatus?.status}
                    required={true}
                  />
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <WsButton onClick={() => setShowOnHoldDialog(false)} variant="outlined">
                Cancel
              </WsButton>
              <WsButton type="submit">Proceed</WsButton>
            </DialogActions>
          </form>
        </FormProvider>
      </WsDialog>
    </Grid>
  );
};

export default InvoiceDetails;
